import { useTranslation } from "react-i18next";

import { asTradeSide, type TradeItem } from "@/entities/robot";
import { formatPercent, formatSlot, moneySign } from "@/shared/lib";
import { Panel } from "@/shared/ui";

/**
 * Последние сделки (ТЗ §11).
 *
 * Строки, а не `<table>`: на 320 px таблица из четырёх колонок либо ужимает
 * пару до нечитаемого, либо выносит экран за правый край (§22).
 *
 * Проценты сделок дня в сумме дают процент дня — их раскладывает бэкенд, и
 * лента поэтому не может разойтись с графиком выше. Спотовый робот показывает
 * только LONG: у него нет коротких позиций, и «SHORT» под ярлыком SPOT было бы
 * первым, что заметят.
 */
export function RecentTradesList({ trades }: { trades: TradeItem[] }) {
  const { t } = useTranslation();

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {t("robots.analytics.trades.title")}
      </h2>

      {trades.length === 0 ? (
        <p className="text-caption font-normal text-text-secondary">
          {t("robots.analytics.trades.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {trades.map((trade) => {
            const side = asTradeSide(trade.side);
            const loss = moneySign(trade.result_pct) < 0;
            return (
              <li
                key={`${trade.pair}-${trade.at}`}
                className="flex min-w-0 items-center justify-between gap-3"
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-caption font-semibold text-text-primary">
                    {trade.pair}
                  </span>
                  <span className="text-micro font-medium text-text-tertiary">
                    {formatSlot(trade.at, { withTime: true })}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2.5">
                  <span
                    className={[
                      "rounded-md px-1.5 py-0.5 text-micro font-semibold",
                      side === "long"
                        ? "bg-status-profit/15 text-status-profit"
                        : "bg-status-loss/15 text-status-loss",
                    ].join(" ")}
                  >
                    {t(`robots.analytics.trades.side.${side}`)}
                  </span>
                  <span
                    className={[
                      "text-caption font-semibold tabular-nums",
                      loss ? "text-status-loss" : "text-status-profit",
                    ].join(" ")}
                  >
                    {formatPercent(trade.result_pct, { sign: "always" })}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
